import { z } from "zod";
import { eidHandoffRequestSchema, emailOtpStartRequestSchema, emailOtpVerifyRequestSchema, eidStartRequestSchema, eidStartResponseSchema, meResponseSchema, passkeyLoginVerifySchema, passkeyRegistrationVerifySchema, passkeySummarySchema, sessionResponseSchema, updateMeRequestSchema, } from "./auth.js";
import { chatMessagePageSchema, chatMessageSchema, chatSummarySchema, chatThreadSchema, clinicThreadSummarySchema, } from "./chat.js";
import { scheduledMessageListResponseSchema, scheduledMessageSchema, } from "./scheduled-messages.js";
import { symptomCheckinSchema, } from "./symptoms.js";
import { readLabResultSchema, selfReportedResultSchema, labReportFileSchema, } from "./labs.js";
import { contactRequestSchema } from "./contact.js";
import { ApiError, errorResponseSchema } from "./errors.js";
import { eventBatchSchema } from "./events.js";
import { healthResponseSchema } from "./health.js";
import { homeSurfaceSchema } from "./home.js";
import { readClientPlanSchema } from "./client-plan.js";
import { readInfusionSurfaceSchema } from "./infusion.js";
import { readSupportSurfaceSchema } from "./support.js";
import { clinicJourneySchema, } from "./journey.js";
import { clientQuestionnaireDetailSchema, questionnaireDetailSchema, questionnaireStateSchema, saveAnswersResponseSchema, } from "./questionnaires.js";
import { questionnaireSummaryListSchema, questionnaireSummarySchema, } from "./questionnaire-summaries.js";
import { patientContactUpdateSchema, patientDetailSchema, patientDetailsSchema, patientListQuerySchema, patientListResponseSchema, patientRelationshipResponseSchema, } from "./patients.js";
import { patientActionCheckInSchema, patientActionsResponseSchema, patientActionSchema, patientActionCheckInCreateSchema, patientActionCreateSchema, staffProfileSchema, } from "./actions.js";
import { patientNoteCreateSchema, patientNoteSchema, } from "./notes.js";
import { onboardingStatusSchema, } from "./onboarding.js";
import { timelineFeedSchema } from "./timeline.js";
import { catalogResponseSchema, checkoutSessionRequestSchema, checkoutSessionResponseSchema, clientOrdersResponseSchema, orderStatusResponseSchema, } from "./commerce.js";
import { supplementCatalogResponseSchema } from "./supplements.js";
export function createApiClient({ baseUrl, getToken, fetch: fetchImpl = globalThis.fetch, onUnauthorized, }) {
    // Defined inside createApiClient, not at module scope, so the 401 choke
    // point can reach this client's own onUnauthorized without threading it
    // through every one of the ~30 call sites below.
    async function throwIfError(res) {
        if (res.ok) {
            return;
        }
        if (res.status === 401) {
            onUnauthorized?.();
        }
        const body = await res.json().catch(() => undefined);
        const parsedError = errorResponseSchema.safeParse(body);
        if (parsedError.success) {
            throw new ApiError(res.status, parsedError.data.error.code, parsedError.data.error.message);
        }
        throw new ApiError(res.status, "unknown_error", "Request failed");
    }
    // The input type is left open rather than pinned to T: a schema carrying a
    // `.default()` parses from a shape that doesn't have the defaulted key, and
    // the wire body is untyped JSON regardless.
    async function parseResponse(res, schema) {
        await throwIfError(res);
        const body = await res.json().catch(() => undefined);
        return schema.parse(body);
    }
    async function parseVoidResponse(res) {
        await throwIfError(res);
    }
    function headers() {
        const result = { "content-type": "application/json" };
        const token = getToken?.();
        if (token) {
            result.authorization = `Bearer ${token}`;
        }
        return result;
    }
    async function health() {
        const res = await fetchImpl(`${baseUrl}/health`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, healthResponseSchema);
    }
    async function startEid(input) {
        const body = eidStartRequestSchema.parse(input);
        const res = await fetchImpl(`${baseUrl}/auth/eid/start`, {
            method: "POST",
            headers: headers(),
            body: JSON.stringify(body),
        });
        return parseResponse(res, eidStartResponseSchema);
    }
    async function redeemHandoff(input) {
        const body = eidHandoffRequestSchema.parse(input);
        const res = await fetchImpl(`${baseUrl}/auth/eid/handoff`, {
            method: "POST",
            headers: headers(),
            body: JSON.stringify(body),
        });
        return parseResponse(res, sessionResponseSchema);
    }
    async function startEmailOtp(input) {
        const body = emailOtpStartRequestSchema.parse(input);
        const res = await fetchImpl(`${baseUrl}/auth/email/start`, {
            method: "POST",
            headers: headers(),
            body: JSON.stringify(body),
        });
        return parseVoidResponse(res);
    }
    async function verifyEmailOtp(input) {
        const body = emailOtpVerifyRequestSchema.parse(input);
        const res = await fetchImpl(`${baseUrl}/auth/email/verify`, {
            method: "POST",
            headers: headers(),
            body: JSON.stringify(body),
        });
        return parseResponse(res, sessionResponseSchema);
    }
    async function logout() {
        const res = await fetchImpl(`${baseUrl}/auth/logout`, {
            method: "POST",
            headers: headers(),
        });
        return parseVoidResponse(res);
    }
    async function me() {
        const res = await fetchImpl(`${baseUrl}/me`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, meResponseSchema);
    }
    async function updateMe(input) {
        const body = updateMeRequestSchema.parse(input);
        const res = await fetchImpl(`${baseUrl}/me`, {
            method: "PATCH",
            headers: headers(),
            body: JSON.stringify(body),
        });
        return parseResponse(res, meResponseSchema);
    }
    async function collectEvents(batch) {
        const body = eventBatchSchema.parse(batch);
        const res = await fetchImpl(`${baseUrl}/events/collect`, {
            method: "POST",
            headers: headers(),
            body: JSON.stringify(body),
        });
        return parseVoidResponse(res);
    }
    async function registrationOptions() {
        const res = await fetchImpl(`${baseUrl}/auth/passkeys/register/options`, {
            method: "POST",
            headers: headers(),
        });
        await throwIfError(res);
        return (await res.json());
    }
    async function verifyRegistration(input) {
        const body = passkeyRegistrationVerifySchema.parse(input);
        const res = await fetchImpl(`${baseUrl}/auth/passkeys/register/verify`, {
            method: "POST",
            headers: headers(),
            body: JSON.stringify(body),
        });
        return parseResponse(res, passkeySummarySchema);
    }
    async function loginOptions() {
        const res = await fetchImpl(`${baseUrl}/auth/passkeys/login/options`, {
            method: "POST",
            headers: headers(),
        });
        await throwIfError(res);
        return (await res.json());
    }
    async function verifyLogin(input) {
        const body = passkeyLoginVerifySchema.parse(input);
        const res = await fetchImpl(`${baseUrl}/auth/passkeys/login/verify`, {
            method: "POST",
            headers: headers(),
            body: JSON.stringify(body),
        });
        return parseResponse(res, sessionResponseSchema);
    }
    async function list() {
        const res = await fetchImpl(`${baseUrl}/auth/passkeys`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, z.array(passkeySummarySchema));
    }
    async function remove(id) {
        const res = await fetchImpl(`${baseUrl}/auth/passkeys/${id}`, {
            method: "DELETE",
            headers: headers(),
        });
        return parseVoidResponse(res);
    }
    async function listPatients(params = {}) {
        const parsed = patientListQuerySchema.parse(params);
        const qs = new URLSearchParams();
        if (parsed.limit !== undefined)
            qs.set("limit", String(parsed.limit));
        if (parsed.cursor !== undefined)
            qs.set("cursor", parsed.cursor);
        if (parsed.track !== undefined)
            qs.set("track", parsed.track);
        const suffix = qs.size > 0 ? `?${qs.toString()}` : "";
        const res = await fetchImpl(`${baseUrl}/clinic/patients${suffix}`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, patientListResponseSchema);
    }
    async function getPatient(id) {
        const res = await fetchImpl(`${baseUrl}/clinic/patients/${id}`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, patientDetailSchema);
    }
    async function getPatientDetails(id) {
        const res = await fetchImpl(`${baseUrl}/clinic/patients/${id}/details`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, patientDetailsSchema);
    }
    async function updatePatientContact(id, input) {
        const body = patientContactUpdateSchema.parse(input);
        const res = await fetchImpl(`${baseUrl}/clinic/patients/${id}/contact`, {
            method: "PATCH",
            headers: headers(),
            body: JSON.stringify(body),
        });
        return parseResponse(res, patientDetailsSchema);
    }
    async function resetPatientOnboarding(id) {
        const res = await fetchImpl(`${baseUrl}/clinic/patients/${id}/onboarding/reset`, {
            method: "POST",
            headers: headers(),
        });
        return parseResponse(res, patientDetailsSchema);
    }
    async function getPatientTimeline(id) {
        const res = await fetchImpl(`${baseUrl}/clinic/patients/${id}/timeline`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, timelineFeedSchema);
    }
    async function getPatientLabs(id) {
        const res = await fetchImpl(`${baseUrl}/clinic/patients/${id}/labs`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, z.array(readLabResultSchema));
    }
    async function getPatientLabReportFiles(id) {
        const res = await fetchImpl(`${baseUrl}/clinic/patients/${id}/lab-report-files`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, z.array(labReportFileSchema));
    }
    async function getPatientLabReportFileContent(id, fileId) {
        const res = await fetchImpl(`${baseUrl}/clinic/patients/${id}/lab-report-files/${fileId}/content`, {
            method: "GET",
            headers: headers(),
        });
        await throwIfError(res);
        return res.blob();
    }
    async function getPatientQuestionnairePhotoContent(id, photoId) {
        const res = await fetchImpl(`${baseUrl}/clinic/patients/${id}/questionnaire-photos/${photoId}/content`, {
            method: "GET",
            headers: headers(),
        });
        await throwIfError(res);
        return res.blob();
    }
    async function getPatientQuestionnaire(id) {
        const res = await fetchImpl(`${baseUrl}/clinic/patients/${id}/questionnaire`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, questionnaireDetailSchema);
    }
    async function getPatientNotes(id) {
        const res = await fetchImpl(`${baseUrl}/clinic/patients/${id}/notes`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, z.array(patientNoteSchema));
    }
    async function addPatientNote(id, input) {
        const body = patientNoteCreateSchema.parse(input);
        const res = await fetchImpl(`${baseUrl}/clinic/patients/${id}/notes`, {
            method: "POST",
            headers: headers(),
            body: JSON.stringify(body),
        });
        return parseResponse(res, patientNoteSchema);
    }
    async function getPatientActions(id) {
        const res = await fetchImpl(`${baseUrl}/clinic/patients/${id}/actions`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, patientActionsResponseSchema);
    }
    async function addPatientAction(id, input) {
        const body = patientActionCreateSchema.parse(input);
        const res = await fetchImpl(`${baseUrl}/clinic/patients/${id}/actions`, {
            method: "POST",
            headers: headers(),
            body: JSON.stringify(body),
        });
        return parseResponse(res, patientActionSchema);
    }
    async function addPatientActionCheckIn(id, actionId, input) {
        const body = patientActionCheckInCreateSchema.parse(input);
        const res = await fetchImpl(`${baseUrl}/clinic/patients/${id}/actions/${actionId}/check-ins`, {
            method: "POST",
            headers: headers(),
            body: JSON.stringify(body),
        });
        return parseResponse(res, patientActionCheckInSchema);
    }
    async function clinicStaffMe() {
        const res = await fetchImpl(`${baseUrl}/clinic/staff/me`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, staffProfileSchema);
    }
    async function getPatientJourney(id) {
        const res = await fetchImpl(`${baseUrl}/clinic/patients/${id}/journey`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, clinicJourneySchema);
    }
    async function ensurePatientRelationship() {
        const res = await fetchImpl(`${baseUrl}/me/patient-relationship`, {
            method: "POST",
            headers: headers(),
        });
        return parseResponse(res, patientRelationshipResponseSchema);
    }
    async function getOnboarding() {
        const res = await fetchImpl(`${baseUrl}/me/onboarding`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, onboardingStatusSchema);
    }
    async function updateOnboarding(input) {
        const res = await fetchImpl(`${baseUrl}/me/onboarding`, {
            method: "PATCH",
            headers: headers(),
            body: JSON.stringify(input),
        });
        return parseResponse(res, onboardingStatusSchema);
    }
    async function completeOnboarding() {
        const res = await fetchImpl(`${baseUrl}/me/onboarding/complete`, {
            method: "POST",
            headers: headers(),
        });
        return parseResponse(res, onboardingStatusSchema);
    }
    async function chatThread() {
        const res = await fetchImpl(`${baseUrl}/chat/thread`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, chatThreadSchema);
    }
    async function chatMessages(after) {
        const query = after !== undefined ? `?after=${after}` : "";
        const res = await fetchImpl(`${baseUrl}/chat/thread/messages${query}`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, chatMessagePageSchema);
    }
    async function chatSend(input) {
        const res = await fetchImpl(`${baseUrl}/chat/thread/messages`, {
            method: "POST",
            headers: headers(),
            body: JSON.stringify(input),
        });
        return parseResponse(res, chatMessageSchema);
    }
    async function chatSummary() {
        const res = await fetchImpl(`${baseUrl}/chat/summary`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, chatSummarySchema);
    }
    async function chatMarkRead(upToSeq) {
        const res = await fetchImpl(`${baseUrl}/chat/thread/read`, {
            method: "POST",
            headers: headers(),
            body: JSON.stringify({ upToSeq }),
        });
        return parseVoidResponse(res);
    }
    async function homeGet() {
        const res = await fetchImpl(`${baseUrl}/client/home`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, homeSurfaceSchema);
    }
    async function planGet() {
        const res = await fetchImpl(`${baseUrl}/client/plan`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, readClientPlanSchema);
    }
    async function infusionGet() {
        const res = await fetchImpl(`${baseUrl}/client/journey/infusion`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, readInfusionSurfaceSchema);
    }
    async function supportGet() {
        const res = await fetchImpl(`${baseUrl}/client/journey/support`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, readSupportSurfaceSchema);
    }
    async function questionnaireGet(instrument) {
        const res = await fetchImpl(`${baseUrl}/client/questionnaires/${instrument}`, {
            method: "GET",
            headers: headers(),
        });
        await throwIfError(res);
        return clientQuestionnaireDetailSchema.parse(await res.json());
    }
    async function questionnaireStart(instrument, consent) {
        const res = await fetchImpl(`${baseUrl}/client/questionnaires/${instrument}/start`, {
            method: "POST",
            headers: headers(),
            body: JSON.stringify(consent ? { consent } : {}),
        });
        await throwIfError(res);
        return questionnaireStateSchema.parse(await res.json());
    }
    async function questionnaireSaveAnswers(instrument, payload) {
        const res = await fetchImpl(`${baseUrl}/client/questionnaires/${instrument}/answers`, {
            method: "POST",
            headers: headers(),
            body: JSON.stringify(payload),
        });
        await throwIfError(res);
        return saveAnswersResponseSchema.parse(await res.json());
    }
    async function questionnaireSubmit(instrument) {
        const res = await fetchImpl(`${baseUrl}/client/questionnaires/${instrument}/submit`, {
            method: "POST",
            headers: headers(),
        });
        await throwIfError(res);
        return questionnaireStateSchema.parse(await res.json());
    }
    async function questionnaireDeleteItem(instrument, questionKey, itemIndex) {
        const res = await fetchImpl(`${baseUrl}/client/questionnaires/${instrument}/items/${questionKey}/${itemIndex}`, { method: "DELETE", headers: headers() });
        await throwIfError(res);
        return questionnaireStateSchema.parse(await res.json());
    }
    async function questionnaireUploadPhoto(instrument, questionKey, photo) {
        const res = await fetchImpl(`${baseUrl}/client/questionnaires/${instrument}/photos?questionKey=${encodeURIComponent(questionKey)}`, { method: "POST", headers: { ...headers(), "content-type": "image/jpeg" }, body: photo });
        return parseResponse(res, z.object({ id: z.string().uuid() }));
    }
    async function questionnairePhotoContent(instrument, photoId) {
        const res = await fetchImpl(`${baseUrl}/client/questionnaires/${instrument}/photos/${photoId}/content`, {
            method: "GET",
            headers: headers(),
        });
        await throwIfError(res);
        return res.blob();
    }
    async function questionnaireSummaryList() {
        const res = await fetchImpl(`${baseUrl}/client/questionnaire-summaries`, {
            method: "GET",
            headers: headers(),
        });
        await throwIfError(res);
        return questionnaireSummaryListSchema.parse(await res.json());
    }
    async function questionnaireSummaryGet(id) {
        const res = await fetchImpl(`${baseUrl}/client/questionnaire-summaries/${encodeURIComponent(id)}`, {
            method: "GET",
            headers: headers(),
        });
        await throwIfError(res);
        return questionnaireSummarySchema.parse(await res.json());
    }
    async function homeDeclare(input) {
        const res = await fetchImpl(`${baseUrl}/client/journey/declarations`, {
            method: "POST",
            headers: headers(),
            body: JSON.stringify(input),
        });
        return parseVoidResponse(res);
    }
    async function homeDismiss(input) {
        const res = await fetchImpl(`${baseUrl}/client/journey/dismissals`, {
            method: "POST",
            headers: headers(),
            body: JSON.stringify(input),
        });
        return parseVoidResponse(res);
    }
    // Both arms POST the same endpoint — the server discriminates on `kind`.
    async function saveSymptomCheckin(input) {
        const res = await fetchImpl(`${baseUrl}/symptoms/checkins`, {
            method: "POST",
            headers: headers(),
            body: JSON.stringify(input),
        });
        return parseResponse(res, symptomCheckinSchema);
    }
    async function saveSelfReportedResult(input) {
        const res = await fetchImpl(`${baseUrl}/labs/results/self-reported`, {
            method: "POST",
            headers: headers(),
            body: JSON.stringify(input),
        });
        return parseResponse(res, selfReportedResultSchema);
    }
    async function labsList() {
        const res = await fetchImpl(`${baseUrl}/labs/results`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, z.array(readLabResultSchema));
    }
    async function uploadLabReportFile(file, collectedOn) {
        const res = await fetchImpl(`${baseUrl}/labs/report-files?collectedOn=${encodeURIComponent(collectedOn)}`, {
            method: "POST",
            headers: { ...headers(), "content-type": "application/pdf" },
            body: file,
        });
        return parseResponse(res, labReportFileSchema);
    }
    async function listLabReportFiles() {
        const res = await fetchImpl(`${baseUrl}/labs/report-files`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, z.array(labReportFileSchema));
    }
    async function labReportFileContent(id) {
        const res = await fetchImpl(`${baseUrl}/labs/report-files/${id}/content`, {
            method: "GET",
            headers: headers(),
        });
        await throwIfError(res);
        return res.blob();
    }
    async function clinicChatSummaries() {
        const res = await fetchImpl(`${baseUrl}/clinic/chat/summaries`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, z.array(clinicThreadSummarySchema));
    }
    async function clinicChatThread(patientId) {
        const res = await fetchImpl(`${baseUrl}/clinic/patients/${patientId}/chat`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, chatThreadSchema);
    }
    async function clinicChatMessages(patientId, after) {
        const query = after !== undefined ? `?after=${after}` : "";
        const res = await fetchImpl(`${baseUrl}/clinic/patients/${patientId}/chat/messages${query}`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, chatMessagePageSchema);
    }
    async function clinicChatSend(patientId, input) {
        const res = await fetchImpl(`${baseUrl}/clinic/patients/${patientId}/chat/messages`, {
            method: "POST",
            headers: headers(),
            body: JSON.stringify(input),
        });
        return parseResponse(res, chatMessageSchema);
    }
    async function clinicChatMarkRead(patientId, upToSeq) {
        const res = await fetchImpl(`${baseUrl}/clinic/patients/${patientId}/chat/read`, {
            method: "POST",
            headers: headers(),
            body: JSON.stringify({ upToSeq }),
        });
        return parseVoidResponse(res);
    }
    async function clinicChatSetStatus(patientId, status) {
        const res = await fetchImpl(`${baseUrl}/clinic/patients/${patientId}/chat`, {
            method: "PATCH",
            headers: headers(),
            body: JSON.stringify({ status }),
        });
        return parseResponse(res, chatThreadSchema);
    }
    async function clinicListScheduled(patientId) {
        const res = await fetchImpl(`${baseUrl}/clinic/patients/${patientId}/scheduled-messages`, {
            method: "GET",
            headers: headers(),
        });
        return parseResponse(res, scheduledMessageListResponseSchema);
    }
    async function clinicSchedule(patientId, input) {
        const res = await fetchImpl(`${baseUrl}/clinic/patients/${patientId}/scheduled-messages`, {
            method: "POST",
            headers: headers(),
            body: JSON.stringify(input),
        });
        return parseResponse(res, scheduledMessageSchema);
    }
    async function clinicUpdateScheduled(patientId, id, input) {
        const res = await fetchImpl(`${baseUrl}/clinic/patients/${patientId}/scheduled-messages/${id}`, {
            method: "PATCH",
            headers: headers(),
            body: JSON.stringify(input),
        });
        return parseResponse(res, scheduledMessageSchema);
    }
    async function clinicSendScheduledNow(patientId, id) {
        const res = await fetchImpl(`${baseUrl}/clinic/patients/${patientId}/scheduled-messages/${id}/send`, {
            method: "POST",
            headers: headers(),
            body: "{}",
        });
        return parseResponse(res, scheduledMessageSchema);
    }
    async function clinicCancelScheduled(patientId, id) {
        const res = await fetchImpl(`${baseUrl}/clinic/patients/${patientId}/scheduled-messages/${id}/cancel`, {
            method: "POST",
            headers: headers(),
            body: "{}",
        });
        return parseResponse(res, scheduledMessageSchema);
    }
    async function getCatalog() {
        const res = await fetchImpl(`${baseUrl}/commerce/catalog`, { headers: headers() });
        return parseResponse(res, catalogResponseSchema);
    }
    async function createCheckoutSession(input) {
        const body = checkoutSessionRequestSchema.parse(input);
        const res = await fetchImpl(`${baseUrl}/commerce/checkout/sessions`, {
            method: "POST",
            headers: headers(),
            body: JSON.stringify(body),
        });
        return parseResponse(res, checkoutSessionResponseSchema);
    }
    async function getOrderStatus(orderNumber, token) {
        const res = await fetchImpl(`${baseUrl}/commerce/orders/${encodeURIComponent(orderNumber)}?token=${encodeURIComponent(token)}`, { headers: headers() });
        return parseResponse(res, orderStatusResponseSchema);
    }
    async function listMyOrders() {
        const res = await fetchImpl(`${baseUrl}/commerce/me/orders`, { headers: headers() });
        return parseResponse(res, clientOrdersResponseSchema);
    }
    async function getSupplementCatalog() {
        const res = await fetchImpl(`${baseUrl}/supplements/catalog`, { headers: headers() });
        return parseResponse(res, supplementCatalogResponseSchema);
    }
    async function submitContact(input) {
        const body = contactRequestSchema.parse(input);
        const res = await fetchImpl(`${baseUrl}/contact`, {
            method: "POST",
            headers: headers(),
            body: JSON.stringify(body),
        });
        return parseVoidResponse(res);
    }
    return {
        health,
        startEid,
        redeemHandoff,
        logout,
        me,
        updateMe,
        collectEvents,
        emailOtp: { start: startEmailOtp, verify: verifyEmailOtp },
        passkeys: { registrationOptions, verifyRegistration, loginOptions, verifyLogin, list, remove },
        onboarding: {
            get: getOnboarding,
            update: updateOnboarding,
            complete: completeOnboarding,
        },
        patients: {
            list: listPatients,
            get: getPatient,
            details: getPatientDetails,
            updateContact: updatePatientContact,
            resetOnboarding: resetPatientOnboarding,
            timeline: getPatientTimeline,
            labs: getPatientLabs,
            labReportFiles: getPatientLabReportFiles,
            labReportFileContent: getPatientLabReportFileContent,
            questionnaire: getPatientQuestionnaire,
            questionnairePhotoContent: getPatientQuestionnairePhotoContent,
            notes: getPatientNotes,
            addNote: addPatientNote,
            actions: getPatientActions,
            addAction: addPatientAction,
            addActionCheckIn: addPatientActionCheckIn,
            journey: getPatientJourney,
            ensureRelationship: ensurePatientRelationship,
        },
        chat: {
            thread: chatThread,
            messages: chatMessages,
            send: chatSend,
            summary: chatSummary,
            markRead: chatMarkRead,
        },
        home: { get: homeGet, declare: homeDeclare, dismiss: homeDismiss },
        plan: { get: planGet },
        infusion: { get: infusionGet },
        support: { get: supportGet },
        questionnaires: {
            get: questionnaireGet,
            start: questionnaireStart,
            saveAnswers: questionnaireSaveAnswers,
            submit: questionnaireSubmit,
            deleteItem: questionnaireDeleteItem,
            uploadPhoto: questionnaireUploadPhoto,
            photoContent: questionnairePhotoContent,
        },
        questionnaireSummaries: { list: questionnaireSummaryList, get: questionnaireSummaryGet },
        symptoms: {
            saveQuick: saveSymptomCheckin,
            saveFull: saveSymptomCheckin,
        },
        labs: {
            saveSelfReported: saveSelfReportedResult,
            list: labsList,
            uploadReportFile: uploadLabReportFile,
            listReportFiles: listLabReportFiles,
            reportFileContent: labReportFileContent,
        },
        commerce: { getCatalog, createCheckoutSession, getOrderStatus, listMyOrders },
        contact: { submit: submitContact },
        supplements: { getCatalog: getSupplementCatalog },
        clinicStaff: { staffMe: clinicStaffMe },
        clinicChat: {
            summaries: clinicChatSummaries,
            thread: clinicChatThread,
            messages: clinicChatMessages,
            send: clinicChatSend,
            markRead: clinicChatMarkRead,
            setStatus: clinicChatSetStatus,
            listScheduled: clinicListScheduled,
            schedule: clinicSchedule,
            updateScheduled: clinicUpdateScheduled,
            sendScheduledNow: clinicSendScheduledNow,
            cancelScheduled: clinicCancelScheduled,
        },
    };
}
